import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  ScanLine, UploadCloud, Loader2, FileText, Check, RotateCcw, Trash2,
  Eye, Inbox, Archive, Search, Building2, Sparkles, Package, Plus,
} from "lucide-react";

const STATUS_LABEL = { pending: "Pending review", verified: "Verified", archived: "Archived" };
const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-300",
  verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
  archived: "bg-slate-100 text-slate-700 border-slate-300",
};

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

export default function ScannedInvoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("inbox"); // 'inbox' | 'all'
  const [scans, setScans] = useState([]);
  const [allScans, setAllScans] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, verified: 0, archived: 0 });
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [verifyOf, setVerifyOf] = useState(null);
  const [verifyForm, setVerifyForm] = useState({ vendor: "", amount: "", invoice_date: "", notes: "" });
  const [savingVerify, setSavingVerify] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [linePreview, setLinePreview] = useState([]);   // [{index, action, name}]
  const fileRef = useRef();

  // All-invoices tab filters
  const [allSupplier, setAllSupplier] = useState("all");
  const [allSearch, setAllSearch] = useState("");
  const [allStatus, setAllStatus] = useState("all");
  const [allFrom, setAllFrom] = useState("");
  const [allTo, setAllTo] = useState("");

  const reloadInbox = async () => {
    try {
      const { data } = await api.get("/scanned-invoices", { params: { status: statusFilter } });
      setScans(data.scans || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load scans");
    }
  };
  const reloadAll = async () => {
    try {
      const { data } = await api.get("/scanned-invoices", { params: { status: "all" } });
      setAllScans(data.scans || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load scans");
    }
  };

  useEffect(() => { reloadInbox(); }, [statusFilter]);
  useEffect(() => { if (tab === "all") reloadAll(); }, [tab]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return scans;
    return scans.filter((s) =>
      (s.original_filename || "").toLowerCase().includes(n) ||
      (s.vendor || "").toLowerCase().includes(n) ||
      (s.uploaded_by || "").toLowerCase().includes(n)
    );
  }, [scans, q]);

  const suppliers = useMemo(() => {
    const set = new Set();
    for (const s of allScans) {
      const v = (s.vendor || "").trim();
      if (v) set.add(v);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allScans]);

  const allFiltered = useMemo(() => {
    const n = allSearch.trim().toLowerCase();
    return allScans.filter((s) => {
      if (allSupplier !== "all" && (s.vendor || "") !== allSupplier) return false;
      if (allStatus !== "all" && s.status !== allStatus) return false;
      if (allFrom && (s.uploaded_at || "").slice(0, 10) < allFrom) return false;
      if (allTo && (s.uploaded_at || "").slice(0, 10) > allTo) return false;
      if (n && !(
        (s.original_filename || "").toLowerCase().includes(n) ||
        (s.vendor || "").toLowerCase().includes(n) ||
        (s.notes || "").toLowerCase().includes(n) ||
        (s.uploaded_by || "").toLowerCase().includes(n)
      )) return false;
      return true;
    }).sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
  }, [allScans, allSupplier, allSearch, allStatus, allFrom, allTo]);

  // Group by supplier for the All Invoices tab
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of allFiltered) {
      const v = (s.vendor || "").trim() || "— Unassigned —";
      if (!map.has(v)) map.set(v, []);
      map.get(v).push(s);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [allFiltered]);

  const onUpload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post("/scanned-invoices/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setBusy(false);
    if (ok > 0) toast.success(`${ok} scan${ok === 1 ? "" : "s"} uploaded — pending review`);
    if (fileRef.current) fileRef.current.value = "";
    await reloadInbox();
    if (tab === "all") await reloadAll();
  };

  const openPreview = (s) => setPreview(s);

  const startVerify = (s) => {
    setVerifyOf(s);
    setVerifyForm({
      vendor: s.vendor || "",
      amount: s.amount != null ? String(s.amount) : "",
      invoice_date: s.invoice_date || "",
      notes: s.notes || "",
    });
    // Pre-populate line items if already extracted
    const ex = s.extracted?.line_items || [];
    setLineItems(ex.map((li) => ({
      name: li.description || "",
      ndc: li.ndc || "",
      quantity: li.quantity || 0,
      unit: "each",
      lot_number: "",
      expiration_date: "",
      cost_per_unit: li.unit_price || 0,
    })));
  };

  const autoExtract = async () => {
    if (!verifyOf) return;
    setExtracting(true);
    try {
      const { data } = await api.post(`/scanned-invoices/${verifyOf.id}/auto-extract`);
      setVerifyForm({
        vendor: data.vendor || verifyForm.vendor,
        amount: data.amount != null ? String(data.amount) : verifyForm.amount,
        invoice_date: data.invoice_date || verifyForm.invoice_date,
        notes: verifyForm.notes,
      });
      const items = data.extracted?.line_items || [];
      setLineItems(items.map((li) => ({
        name: li.description || "",
        ndc: li.ndc || "",
        quantity: li.quantity || 0,
        unit: "each",
        lot_number: "",
        expiration_date: "",
        cost_per_unit: li.unit_price || 0,
      })));
      toast.success(`AI extracted ${items.length} line item${items.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI extract failed");
    } finally { setExtracting(false); }
  };

  const syncToInventory = async () => {
    if (!verifyOf) return;
    const valid = lineItems.filter((li) => li.name && Number(li.quantity) > 0);
    if (valid.length === 0) return toast.error("Need at least one line with name + quantity");
    setSyncing(true);
    try {
      const { data } = await api.post("/inventory/sync-from-invoice", {
        scanned_invoice_id: verifyOf.id, line_items: valid,
      });
      const s = data.summary || {};
      toast.success(`Inventory synced — created ${s.created || 0}, added ${s.lot_added || 0}, topped-up ${s.topped_up || 0}${s.skipped ? `, skipped ${s.skipped}` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const addLineItem = () => setLineItems((p) => [...p, { name: "", ndc: "", quantity: 0, unit: "each", lot_number: "", expiration_date: "", cost_per_unit: 0 }]);
  const setLineItem = (i, k, v) => setLineItems((p) => { const n = [...p]; n[i] = { ...n[i], [k]: v }; return n; });
  const removeLineItem = (i) => setLineItems((p) => p.filter((_, idx) => idx !== i));

  // Preview sync actions whenever line items change (debounced)
  useEffect(() => {
    if (!verifyOf || lineItems.length === 0) { setLinePreview([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/inventory/sync-from-invoice/preview",
          { line_items: lineItems });
        setLinePreview(data.results || []);
      } catch { setLinePreview([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [lineItems, verifyOf]);

  const saveVerify = async () => {
    if (!verifyOf) return;
    setSavingVerify(true);
    try {
      await api.post(`/scanned-invoices/${verifyOf.id}/verify`, {
        vendor: verifyForm.vendor,
        amount: verifyForm.amount ? Number(verifyForm.amount) : null,
        invoice_date: verifyForm.invoice_date,
        notes: verifyForm.notes,
      });
      toast.success("Scan marked verified");
      setVerifyOf(null);
      await reloadInbox();
      if (tab === "all") await reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setSavingVerify(false); }
  };

  const reopen = async (id) => {
    try {
      await api.post(`/scanned-invoices/${id}/reopen`);
      toast.success("Sent back to pending");
      await reloadInbox();
      if (tab === "all") await reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reopen failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this scan permanently?")) return;
    try {
      await api.delete(`/scanned-invoices/${id}`);
      toast.success("Scan deleted");
      await reloadInbox();
      if (tab === "all") await reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const totalCount = (counts.pending || 0) + (counts.verified || 0) + (counts.archived || 0);

  const rowActions = (s) => (
    <div className="text-right space-x-1 whitespace-nowrap">
      <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
        data-testid={`scan-preview-${s.id}`} onClick={() => openPreview(s)}>
        <Eye className="w-3 h-3" /> Preview
      </Button>
      {s.status === "pending" && isAdmin && (
        <Button size="sm" className="h-7 gap-1 text-xs bg-emerald-700 hover:bg-emerald-800"
          data-testid={`scan-verify-${s.id}`} onClick={() => startVerify(s)}>
          <Check className="w-3 h-3" /> Verify
        </Button>
      )}
      {s.status === "verified" && isAdmin && (
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
          data-testid={`scan-reopen-${s.id}`} onClick={() => reopen(s.id)}>
          <RotateCcw className="w-3 h-3" /> Reopen
        </Button>
      )}
      {isAdmin && (
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-rose-600 hover:text-rose-800 hover:bg-rose-50"
          data-testid={`scan-delete-${s.id}`} onClick={() => remove(s.id)}>
          <Trash2 className="w-3 h-3" />
        </Button>
      )}
    </div>
  );

  const rowCells = (s) => (
    <>
      <TableCell className="text-xs font-mono text-slate-700 whitespace-nowrap">
        {(s.uploaded_at || "").replace("T", " ").slice(0, 16)}
      </TableCell>
      <TableCell className="text-xs text-slate-800 max-w-xs truncate" title={s.original_filename}>
        <FileText className="w-3.5 h-3.5 inline mr-1 text-pink-700" />
        {s.original_filename}
      </TableCell>
      <TableCell className="text-xs text-slate-700">{s.vendor || <span className="text-slate-300">—</span>}</TableCell>
      <TableCell className="text-xs font-mono text-right text-slate-700">
        {s.amount != null ? `$${Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
      </TableCell>
      <TableCell className="text-xs text-slate-700">{s.invoice_date || <span className="text-slate-300">—</span>}</TableCell>
      <TableCell>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[s.status] || STATUS_STYLE.pending}`}>
          {STATUS_LABEL[s.status] || s.status}
        </span>
      </TableCell>
      <TableCell className="text-xs text-slate-500">{s.uploaded_by}</TableCell>
      <TableCell className="text-xs font-mono text-right text-slate-500">{fmtBytes(s.size)}</TableCell>
      <TableCell className="text-right">{rowActions(s)}</TableCell>
    </>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <ScanLine className="w-7 h-7 text-pink-700" /> Scanned Invoices
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Drop the PDFs Epson ScanSmart saves on your PC. Every scan lands in{" "}
          <span className="font-semibold">Pending review</span> — you approve each one before it's filed.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
        {[
          { id: "inbox", label: "Inbox", icon: Inbox, badge: counts.pending || 0 },
          { id: "all", label: "All Invoices", icon: Archive, badge: totalCount },
        ].map((t) => (
          <button key={t.id} data-testid={`scan-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-pink-700 text-pink-800"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            <t.icon className="w-4 h-4" />
            {t.label}
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
              tab === t.id ? "bg-pink-100 text-pink-800" : "bg-slate-100 text-slate-500"
            }`}>{t.badge}</span>
          </button>
        ))}
      </div>

      {tab === "inbox" && (
        <>
          <Card className="p-5 mb-5 border-pink-200" data-testid="scanned-upload-card">
            <button
              data-testid="scanned-upload-btn"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files); }}
              disabled={busy}
              className="w-full p-8 border-2 border-dashed border-pink-200 rounded-xl hover:border-pink-700 hover:bg-pink-50/30 transition-colors flex flex-col items-center gap-2 disabled:opacity-60">
              {busy
                ? <><Loader2 className="w-7 h-7 text-pink-700 animate-spin" /><span className="text-sm text-slate-500">Uploading…</span></>
                : <><UploadCloud className="w-7 h-7 text-pink-700" />
                    <div className="text-center">
                      <div className="font-semibold text-slate-800">Drop scanned invoices here</div>
                      <div className="text-xs text-slate-500 mt-1">PDF, JPG, PNG, TIFF · one or many · click to browse</div>
                    </div></>}
            </button>
            <input
              ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff"
              onChange={(e) => onUpload(e.target.files)} className="hidden"
              data-testid="scanned-upload-input" />
          </Card>

          <Card className="p-3 mb-4 border-slate-200 flex flex-wrap items-center gap-2">
            {["pending", "verified", "archived", "all"].map((s) => (
              <button key={s} data-testid={`scan-filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1 rounded-full border font-medium ${
                  statusFilter === s
                    ? "bg-pink-700 text-white border-pink-700"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}>
                {s === "all" ? `All (${totalCount})`
                  : `${STATUS_LABEL[s] || s} (${counts[s] || 0})`}
              </button>
            ))}
            <Input data-testid="scan-search"
              placeholder="Search filename, vendor, uploader…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="max-w-xs h-8 text-sm ml-auto" />
          </Card>

          <Card className="p-0 overflow-hidden border-slate-200">
            {filtered.length === 0 ? (
              <div className="p-12 text-center text-sm text-slate-500">
                No scans in this view. Upload one above.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="scans-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Uploaded</TableHead>
                      <TableHead>Filename</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Invoice date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Uploaded by</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((s) => (
                      <TableRow key={s.id} data-testid={`scan-row-${s.id}`}>{rowCells(s)}</TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}

      {tab === "all" && (
        <>
          <Card className="p-3 mb-4 border-slate-200" data-testid="all-invoices-filters">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
              <div className="md:col-span-2">
                <label className="text-[10px] text-slate-500 uppercase flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Supplier
                </label>
                <select data-testid="all-supplier" value={allSupplier}
                  onChange={(e) => setAllSupplier(e.target.value)}
                  className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-200 focus:border-pink-700 focus:outline-none focus:ring-1 focus:ring-pink-700 h-9">
                  <option value="all">All suppliers ({suppliers.length})</option>
                  {suppliers.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Status</label>
                <select data-testid="all-status" value={allStatus}
                  onChange={(e) => setAllStatus(e.target.value)}
                  className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-200 focus:border-pink-700 focus:outline-none focus:ring-1 focus:ring-pink-700 h-9">
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">From</label>
                <Input data-testid="all-from" type="date" value={allFrom}
                  onChange={(e) => setAllFrom(e.target.value)} className="h-9 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">To</label>
                <Input data-testid="all-to" type="date" value={allTo}
                  onChange={(e) => setAllTo(e.target.value)} className="h-9 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase flex items-center gap-1">
                  <Search className="w-3 h-3" /> Search
                </label>
                <Input data-testid="all-search" placeholder="filename, vendor, notes…"
                  value={allSearch} onChange={(e) => setAllSearch(e.target.value)}
                  className="h-9 text-xs" />
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
              <span>{allFiltered.length} of {allScans.length} invoices · sorted newest first</span>
              {(allSupplier !== "all" || allStatus !== "all" || allFrom || allTo || allSearch) && (
                <button data-testid="all-clear-filters"
                  onClick={() => { setAllSupplier("all"); setAllStatus("all"); setAllFrom(""); setAllTo(""); setAllSearch(""); }}
                  className="text-pink-700 hover:underline">Clear all filters</button>
              )}
            </div>
          </Card>

          {allSupplier === "all" ? (
            grouped.length === 0 ? (
              <Card className="p-12 text-center text-sm text-slate-500 border-slate-200">
                No invoices match your filters.
              </Card>
            ) : (
              grouped.map(([supplier, rows]) => {
                const supplierTotal = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                return (
                  <Card key={supplier} className="mb-4 border-slate-200 overflow-hidden"
                        data-testid={`supplier-group-${supplier}`}>
                    <div className="flex items-center gap-2 px-4 py-2 bg-pink-50 border-b border-pink-200">
                      <Building2 className="w-4 h-4 text-pink-700" />
                      <span className="font-semibold text-sm text-slate-800">{supplier}</span>
                      <span className="text-xs text-slate-500">{rows.length} invoice{rows.length === 1 ? "" : "s"}</span>
                      {supplierTotal > 0 && (
                        <span className="ml-auto text-xs font-mono text-emerald-800">
                          ${supplierTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 text-xs">
                            <TableHead>Uploaded</TableHead>
                            <TableHead>Filename</TableHead>
                            <TableHead>Vendor</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Invoice date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Uploaded by</TableHead>
                            <TableHead className="text-right">Size</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((s) => (
                            <TableRow key={s.id} data-testid={`all-scan-row-${s.id}`}>{rowCells(s)}</TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                );
              })
            )
          ) : (
            <Card className="p-0 overflow-hidden border-slate-200">
              {allFiltered.length === 0 ? (
                <div className="p-12 text-center text-sm text-slate-500">
                  No invoices match your filters.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Uploaded</TableHead>
                        <TableHead>Filename</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Invoice date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Uploaded by</TableHead>
                        <TableHead className="text-right">Size</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allFiltered.map((s) => (
                        <TableRow key={s.id} data-testid={`all-scan-row-${s.id}`}>{rowCells(s)}</TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-pink-700" />
              {preview?.original_filename}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="flex-1 min-h-0 rounded border border-slate-200 overflow-hidden">
              <iframe
                data-testid="scan-preview-iframe"
                src={`${api.defaults?.baseURL || ""}/scanned-invoices/${preview.id}/download`}
                title="scan preview"
                className="w-full h-full"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify dialog */}
      <Dialog open={!!verifyOf} onOpenChange={(o) => !o && setVerifyOf(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 text-base">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-700" /> Verify scanned invoice
              </span>
              <Button data-testid="ai-extract-btn" onClick={autoExtract} disabled={extracting}
                size="sm" variant="outline" className="h-8 gap-1 text-xs border-pink-300 text-pink-700 hover:bg-pink-50">
                {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                AI auto-extract
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-500 font-mono truncate">{verifyOf?.original_filename}</div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Vendor / Supplier</label>
              <Input data-testid="verify-vendor" value={verifyForm.vendor}
                onChange={(e) => setVerifyForm({ ...verifyForm, vendor: e.target.value })}
                list="verify-vendor-list"
                placeholder="e.g. Cardinal Health" className="h-9 text-sm" />
              <datalist id="verify-vendor-list">
                {suppliers.map((v) => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Amount</label>
                <Input data-testid="verify-amount" type="number" step="0.01"
                  value={verifyForm.amount}
                  onChange={(e) => setVerifyForm({ ...verifyForm, amount: e.target.value })}
                  placeholder="0.00" className="h-9 text-sm font-mono" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Invoice date</label>
                <Input data-testid="verify-date" value={verifyForm.invoice_date}
                  onChange={(e) => setVerifyForm({ ...verifyForm, invoice_date: e.target.value })}
                  placeholder="MM/DD/YYYY" className="h-9 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Notes</label>
              <textarea data-testid="verify-notes" value={verifyForm.notes} rows={2}
                onChange={(e) => setVerifyForm({ ...verifyForm, notes: e.target.value })}
                className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-200 focus:border-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-700"
                placeholder="Optional — GL code, PO#, discrepancies…" />
            </div>

            {/* Inventory sync section */}
            <Card className="p-3 border-pink-200 bg-pink-50/40">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-pink-900">
                  <Package className="w-4 h-4" /> Sync line items to Inventory
                </div>
                <Button size="sm" variant="outline" onClick={addLineItem}
                  data-testid="add-line-item" className="h-7 gap-1 text-xs">
                  <Plus className="w-3 h-3" /> Add line
                </Button>
              </div>
              {lineItems.length === 0 ? (
                <p className="text-[11px] text-slate-500 italic">
                  Click <b>AI auto-extract</b> above to pull line items from the scan, or add rows manually.
                </p>
              ) : (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-12 gap-1 text-[10px] text-slate-500 uppercase font-semibold">
                    <div className="col-span-1">Action</div>
                    <div className="col-span-3">Item</div>
                    <div className="col-span-2">NDC</div>
                    <div className="col-span-2">Lot #</div>
                    <div className="col-span-1 text-right">Qty</div>
                    <div className="col-span-1">Unit</div>
                    <div className="col-span-1 text-right">$/unit</div>
                    <div className="col-span-1"></div>
                  </div>
                  {lineItems.map((li, i) => {
                    const preview = linePreview.find((p) => p.index === i);
                    const badge = preview ? {
                      created: { txt: "NEW", cls: "bg-sky-100 text-sky-800 border-sky-300" },
                      lot_added: { txt: "+ LOT", cls: "bg-violet-100 text-violet-800 border-violet-300" },
                      topped_up: { txt: "TOP-UP", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
                      skipped: { txt: "SKIP", cls: "bg-slate-100 text-slate-500 border-slate-300" },
                    }[preview.action] : null;
                    return (
                      <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`li-row-${i}`}>
                        <div className="col-span-1">
                          {badge ? (
                            <span data-testid={`li-badge-${i}`} title={preview.name || preview.reason || ""}
                              className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${badge.cls}`}>
                              {badge.txt}
                            </span>
                          ) : <span className="text-[9px] text-slate-300">…</span>}
                        </div>
                        <Input value={li.name} onChange={(e) => setLineItem(i, "name", e.target.value)}
                          placeholder="Name/description" className="col-span-3 h-7 text-xs" />
                        <Input value={li.ndc} onChange={(e) => setLineItem(i, "ndc", e.target.value)}
                          placeholder="NDC" className="col-span-2 h-7 text-xs font-mono" />
                        <Input value={li.lot_number} onChange={(e) => setLineItem(i, "lot_number", e.target.value)}
                          placeholder="Lot" className="col-span-2 h-7 text-xs font-mono" />
                        <Input type="number" step="any" value={li.quantity}
                          onChange={(e) => setLineItem(i, "quantity", Number(e.target.value))}
                          className="col-span-1 h-7 text-xs font-mono text-right" />
                        <Input value={li.unit} onChange={(e) => setLineItem(i, "unit", e.target.value)}
                          className="col-span-1 h-7 text-xs" />
                        <Input type="number" step="any" value={li.cost_per_unit}
                          onChange={(e) => setLineItem(i, "cost_per_unit", Number(e.target.value))}
                          className="col-span-1 h-7 text-xs font-mono text-right" />
                        <button onClick={() => removeLineItem(i)} className="col-span-1 text-rose-500 hover:text-rose-700 flex justify-center"
                          data-testid={`li-remove-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between pt-1">
                    <div className="text-[10px] text-slate-500 flex gap-2 flex-wrap">
                      {["created", "lot_added", "topped_up", "skipped"].map((a) => {
                        const n = linePreview.filter((p) => p.action === a).length;
                        if (!n) return null;
                        const cls = a === "created" ? "text-sky-700"
                          : a === "lot_added" ? "text-violet-700"
                          : a === "topped_up" ? "text-emerald-700" : "text-slate-500";
                        return <span key={a} className={cls}><b>{n}</b> {a.replace("_", " ")}</span>;
                      })}
                    </div>
                    <Button size="sm" onClick={syncToInventory} disabled={syncing}
                      data-testid="sync-inventory-btn"
                      className="h-8 gap-1 text-xs bg-pink-700 hover:bg-pink-800">
                      {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
                      Push {lineItems.filter((li) => li.name && Number(li.quantity) > 0).length} to Inventory
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVerifyOf(null)}>Cancel</Button>
            <Button data-testid="verify-save" onClick={saveVerify} disabled={savingVerify}
              className="bg-emerald-700 hover:bg-emerald-800 gap-1.5">
              {savingVerify ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Mark verified
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
